import React from 'react';
import { Box, SystemStyleObject, useColorModeValue } from '@chakra-ui/react';
// eslint-disable-next-line no-restricted-imports
import ReactDatepicker from 'react-datepicker';

const selectStyle: SystemStyleObject = {
  color: 'content.title',
  bgColor: 'surface.sidebarMuted',
  border: '1px solid',
  borderColor: 'surface.border',
  borderRadius: '4px',
  px: '4px',
  py: '2px',
  mt: '10px',
  _focus: {
    outline: 'none',
    borderColor: 'icon.green',
    boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
  },
};

export type StyledDatePickerProps = React.ComponentProps<
  typeof ReactDatepicker
>;

export const StyledDatePicker: React.FC<StyledDatePickerProps> = (props) => {
  // In-range days: translucent brand green in both modes
  const inRangeBg = useColorModeValue(
    'border.lightGreen',
    'rgba(39, 174, 96, 0.35)',
  );

  return (
    <Box
      w="100%"
      sx={{
        '.react-datepicker-wrapper': {
          width: '100%',
        },
        '.react-datepicker': {
          border: 'none',
          fontFamily: 'marketingPage',
          width: '100%',
          bg: 'surface.panel',
        },
        '.react-datepicker__month-container': {
          width: '100%',
        },
        '.react-datepicker__header': {
          bg: 'transparent',
          borderBottom: 'none',
          pt: 4,
          width: '100%',
        },
        '.react-datepicker__current-month': {
          fontWeight: 'bold',
          fontSize: '16px',
          color: 'content.title',
          mb: 2,
        },
        '.react-datepicker__day-names': {
          display: 'flex',
          justifyContent: 'space-around',
          pb: 2,
          width: '100%',
        },
        '.react-datepicker__day-name': {
          color: 'content.muted',
          fontSize: '12px',
          fontWeight: 'normal',
          flex: 1,
          textAlign: 'center',
          lineHeight: '36px',
        },
        '.react-datepicker__month': {
          m: 0,
          p: 4,
          width: '100%',
        },
        '.react-datepicker__week': {
          display: 'flex',
          justifyContent: 'space-around',
          width: '100%',
        },
        '.react-datepicker__day': {
          flex: 1,
          height: '36px',
          lineHeight: '36px',
          borderRadius: 'md',
          fontSize: '14px',
          color: 'content.body',
          m: 0,
          textAlign: 'center',
          _hover: {
            bg: 'border.lightGreen',
            color: 'green.600',
          },
        },
        '.react-datepicker__day--selected': {
          bg: 'icon.green !important',
          color: 'white !important',
          fontWeight: 'bold',
        },
        '.react-datepicker__day--in-range': {
          bg: `${inRangeBg} !important`,
          color: 'content.title !important',
        },
        '.react-datepicker__day--range-start': {
          bg: 'icon.green !important',
          color: 'white !important',
          borderTopLeftRadius: 'md',
          borderBottomLeftRadius: 'md',
        },
        '.react-datepicker__day--range-end': {
          bg: 'icon.green !important',
          color: 'white !important',
          borderTopRightRadius: 'md',
          borderBottomRightRadius: 'md',
        },
        '.react-datepicker__day--in-selecting-range': {
          bg: `${inRangeBg} !important`,
          color: 'content.title !important',
        },
        // Library default paints today/keyboard-selected with its own light
        // backgrounds — neutralize to theme surfaces
        '.react-datepicker__day--today': {
          fontWeight: 'bold',
          color: 'content.title',
        },
        '.react-datepicker__day--keyboard-selected': {
          bg: 'surface.sidebarMuted',
          color: 'content.title',
        },
        '.react-datepicker__day--outside-month': {
          color: 'content.muted',
        },
        '.react-datepicker__day--disabled': {
          color: 'content.muted',
          opacity: 0.5,
          cursor: 'not-allowed',
        },
        '.react-datepicker__navigation': {
          top: '20px',
        },
        '.react-datepicker__navigation-icon::before': {
          borderColor: 'content.muted',
        },
        '.react-datepicker__month-select': selectStyle,
        '.react-datepicker__year-select': selectStyle,
      }}>
      <ReactDatepicker {...props} />
    </Box>
  );
};
